import type { SeedanceProviderConfigView } from "@actale/contracts";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

export function SeedanceProviderConfigPanel({ refreshToken = 0 }: { refreshToken?: number }): React.ReactNode {
  const [config, setConfig] = useState<SeedanceProviderConfigView | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setConfig(await api.seedanceProviderConfig());
      setDeleteConfirm(false);
    } catch (caught) {
      setError(message(caught, "无法读取火山方舟配置"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, refreshToken]);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!config || !apiKey.trim()) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const next = await api.updateSeedanceProviderConfig({
        apiKey: apiKey.trim(),
        expectedUpdatedAt: config.updatedAt,
      });
      setConfig(next);
      setApiKey("");
      setShowKey(false);
      setNotice("火山方舟 Key 已加密保存；视频生成页不会读取或显示密钥。");
    } catch (caught) {
      setError(message(caught, "火山方舟 Key 保存失败"));
    } finally {
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    if (!config?.updatedAt || !deleteConfirm) return;
    setDeleting(true);
    setError("");
    setNotice("");
    try {
      await api.deleteSeedanceProviderConfig(config.updatedAt);
      await load();
      setNotice("火山方舟 Key 密文已删除；已有视频任务记录仍保留。");
    } catch (caught) {
      setError(message(caught, "删除火山方舟 Key 失败"));
    } finally {
      setDeleting(false);
    }
  }

  if (loading && !config) {
    return <div className="director-loading"><span /><strong>正在读取火山方舟配置…</strong></div>;
  }

  if (!config) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "火山方舟配置不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  const busy = loading || saving || deleting;

  return (
    <section className="seedance-key-card provider-seedance-card">
      <header>
        <div>
          <span className={`provider-state-dot ${config.configured ? "ready" : "empty"}`} />
          <div><strong>视频生成模型 · Seedance 2.5</strong><small>{config.provider} · {config.modelName}</small></div>
        </div>
        <span className={`provider-config-status ${config.configured ? "ready" : "empty"}`}>{config.configured ? "已配置" : "未配置"}</span>
      </header>

      {error ? <div className="error-notice provider-inline-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice provider-inline-notice" role="status">✓ {notice}</div> : null}
      {!config.encryptionReady ? (
        <div className="provider-security-alert provider-inline-notice" role="alert">
          <strong>服务器加密主密钥尚未就绪</strong>
          <span>在服务器配置主密钥并重启 API 后，才能保存火山方舟 Key。</span>
        </div>
      ) : null}

      <form onSubmit={(event) => void save(event)}>
        <label>
          <span>{config.configured ? "API Key（留空表示不更换）" : "API Key"}</span>
          <div className="provider-secret-input">
            <input type={showKey ? "text" : "password"} value={apiKey} onChange={(event) => setApiKey(event.target.value)} autoComplete="new-password" minLength={8} maxLength={8192} placeholder={config.configured ? `已保存 ${config.apiKeyHint ?? "加密密钥"}` : "输入火山方舟 API Key"} required />
            <button type="button" className="text-button" onClick={() => setShowKey(!showKey)}>{showKey ? "隐藏" : "显示"}</button>
          </div>
        </label>
        <button className="primary-button compact" disabled={busy || !config.encryptionReady || !apiKey.trim()}>{saving ? "正在加密保存…" : config.configured ? "安全更换 Key" : "安全保存 Key"}</button>
        <small className="seedance-key-help">固定接口：{config.apiBaseUrl}。Key 使用 AES-256-GCM 在服务端加密，页面和接口不返回明文。</small>
      </form>

      <footer>
        <span>{config.updatedAt ? `最近更新：${formatUpdatedAt(config.updatedAt)} · ${config.updatedBy?.displayName ?? "系统"}` : "尚无已保存配置"}</span>
        {config.configured ? (!deleteConfirm ? <button className="danger-text-button" type="button" onClick={() => setDeleteConfirm(true)}>删除 Seedance 配置</button> : <span className="seedance-delete-confirm"><button className="danger-button" type="button" disabled={busy} onClick={() => void remove()}>{deleting ? "正在删除…" : "确认删除"}</button><button className="text-button" type="button" onClick={() => setDeleteConfirm(false)}>取消</button></span>) : null}
      </footer>
    </section>
  );
}
